/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { isArray } from 'lodash';
import { Box, Typography, Avatar, Chip, Button, alpha, TextField, InputAdornment, IconButton, Tooltip, Popover, List, ListItemButton, ListItemText, Checkbox, Divider, Badge } from '@mui/material';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Iconify } from 'src/components';
import { dispatch, useSelector as useReduxSelector } from 'src/redux/store';
import { setSelectedUserId, COMPARISON_FIELDS } from 'src/redux/slices/userPerformance';
import {
    getTablePreferences,
    handleTablePreferences,
    handleTablePreferencesEdit,
    setTablePreferenceLoading,
} from 'src/redux/slices/tablePreferences';
import { getCurrentTableData } from 'src/utils/tablePreferences';
import useGetCurrentUserId from 'src/hooks/useGetCurrentUserId';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { computeDelta } from 'src/utils/comparisonPresets';
import moment from 'moment';
import DeltaChip from './DeltaChip';
import TreatmentLeadPopup from './TreatmentLeadPopup';

// Used for table-preferences persistence (matches LandingUrlTable /
// SourceStatusTable / ReferralStatusTable pattern). Changing this string
// orphans existing user preferences in the DB, so don't change it.
const TABLE_NAME = 'User Performance Table';

// Field → label mapping for the column-visibility Popover + CSV header
// row. Listed in display order. `name` is omitted because it's not
// hideable — the table makes no sense without identifying who's on each
// row.
// Display order: client direction — Appts Booked and Archived are the
// two metrics that matter most, so they sit immediately after User Name.
// The rest of the funnel + activity stack follows. The order here drives
// both the DataGrid column sequence AND the Popover checkbox order, so
// the user sees the same arrangement whether they're reading the table
// or toggling visibility.
const ALL_COLUMNS = [
    { field: 'appointmentsBooked', label: 'Appts Booked' },
    { field: 'archived', label: 'Archived' },
    { field: 'totalEvents', label: 'Total Events' },
    { field: 'leadsContacted', label: 'Contacted' },
    { field: 'bookRate', label: 'Book Rate' },
    { field: 'attendRate', label: 'Attend Rate' },
    { field: 'appointmentsAttended', label: 'Appts Attended' },
    { field: 'fta', label: 'FTA' },
    { field: 'followUps', label: 'Follow-ups' },
    { field: 'whatsappSent', label: 'WA Sent' },
    { field: 'whatsappReceived', label: 'WA Recd' },
    { field: 'emails', label: 'Emails' },
    { field: 'sms', label: 'SMS' },
    { field: 'calls', label: 'Calls' },
    { field: 'payments', label: 'Payments' },
];

const COLUMN_COLORS = {
    totalEvents: '#6366f1',
    leadsContacted: '#3b82f6',
    appointmentsBooked: '#8b5cf6',
    appointmentsAttended: '#10b981',
    fta: '#ef4444',
    followUps: '#f59e0b',
    whatsappSent: '#22c55e',
    whatsappReceived: '#14b8a6',
    emails: '#6366f1',
    sms: '#ec4899',
    calls: '#f97316',
    payments: '#10b981',
};

// Cell drilldown -> the UserEvent type its counter aggregates
// (apps/reports/views/user_performance.py:141). These columns count events BY
// ACTOR, so the drilldown must read UserEvent too — filtering leads by assignee
// cannot reconcile with them and returned 0 rows for every user.
//
// Activity counters (WA Sent / Emails / Calls / Payments / Follow-ups /
// Total Events / Contacted) stay non-clickable per 5c892fd7e.
export const FIELD_TO_EVENT_TYPE = {
    appointmentsBooked: 'APPOINTMENT_BOOKED',
    appointmentsAttended: 'APPOINTMENT_ATTENDED',
    fta: 'APPOINTMENT_FTA',
    archived: 'LEAD_ARCHIVED',
};

// Popup heading label per drillable column — the FIELD_TO_EVENT_TYPE brief
// (RULING B) referenced a COLUMN_LABEL map that didn't exist in this file;
// defined here so the popup heading reads "User — Appointments Booked"
// instead of the raw field key.
const COLUMN_LABEL = {
    appointmentsBooked: 'Appointments Booked',
    appointmentsAttended: 'Appointments Attended',
    fta: 'Failed to Attend',
    archived: 'Leads Archived',
};

const getInitials = (name) => {
    if (!name) return '?';
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
};

// rateCell renders a derived percentage (e.g. Book Rate = Booked /
// Contacted) as a coloured chip. Distinct from numCellWithDelta because:
//   - The underlying value isn't an event count → no drill-down
//   - It's a derived ratio → no Δ chip in v1 (Δ on a ratio is two-input
//     and harder to interpret; defer until we know users want it)
//   - null denominator (e.g. user had no contacts) renders as "—"
// Threshold colours: green ≥ 50%, amber 20–49%, red < 20%. The bands are
// rough team-lead heuristics, not statistical; tune by feedback.
const rateCell = () => ({
    renderCell: (params) => {
        const { value } = params;
        if (value === null || value === undefined) {
            return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>—</Typography>;
        }
        let color = '#ef4444'; // red
        let bg = alpha('#ef4444', 0.12);
        if (value >= 50) { color = '#10b981'; bg = alpha('#10b981', 0.12); }
        else if (value >= 20) { color = '#f59e0b'; bg = alpha('#f59e0b', 0.12); }
        return (
            <Chip
                label={`${Math.round(value)}%`}
                size="small"
                sx={{
                    bgcolor: bg,
                    color,
                    fontWeight: 600,
                    fontSize: '12px',
                    height: 26,
                    minWidth: 50,
                }}
            />
        );
    },
});

// numCellWithDelta renders the numeric value as a chip with a sibling DeltaChip
// fed by the row's `previous_*` field. Preserves:
//   - Existing colour mapping per metric
//   - Zero-value non-clickability (value=0 → chip is dimmed, no pointer)
//   - Stop-propagation on click so row-click handlers don't fight cell click
//
// The Δ chip only renders when previous data is present on the row —
// during the brief gap while the previous-period fetch is still in flight
// (or for users who didn't exist in the previous period) the cell shows
// the number alone with no chip. This keeps the table calm rather than
// flashing em-dashes everywhere on every fetch.
const numCellWithDelta = (field, prevField, onCellClick, { inverted = false, drillable = true } = {}) => ({
    renderCell: (params) => {
        const value = params.value || 0;
        const prev = params.row?.[prevField];
        const color = COLUMN_COLORS[field] || '#64748b';
        // Clickable when:
        //   1. The cell maps to a meaningful lead list (drillable=true).
        //      Activity counters like "WA Sent" / "Emails" / "Calls" set
        //      drillable=false because they're aggregate event counts —
        //      no honest single-lead-list interpretation. Better to be
        //      non-clickable than open a popup with misleading filters.
        //   2. EITHER current or previous value is > 0. The value=0 +
        //      prev>0 case is especially useful: surfaces "user used to
        //      do this and stopped"; clicking confirms the empty state.
        const clickable = drillable
            && (value > 0 || (typeof prev === 'number' && prev > 0))
            && onCellClick;
        const delta = prev !== undefined ? computeDelta(value, prev) : null;
        // Render the chip only when previous data exists. `null` from
        // computeDelta (prev=0, current>0) is still a renderable state —
        // DeltaChip handles it as "—".
        const showChip = prev !== undefined;
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center', width: '100%' }}>
                <Chip
                    label={value}
                    size="small"
                    onClick={clickable ? (e) => {
                        e.stopPropagation();
                        onCellClick(params.row, field);
                    } : undefined}
                    sx={{
                        bgcolor: value > 0 ? alpha(color, 0.12) : 'transparent',
                        color: value > 0 ? color : 'text.disabled',
                        fontWeight: 600,
                        fontSize: '12px',
                        height: 26,
                        minWidth: 40,
                        ...(clickable && {
                            cursor: 'pointer',
                            '&:hover': { bgcolor: alpha(color, 0.22) },
                        }),
                    }}
                />
                {showChip && <DeltaChip value={delta} inverted={inverted} />}
            </Box>
        );
    },
});


// --- Row-expand detail panel: per-user channel split ---
//
// Shows HOW a user is reaching their contacts — phone vs WhatsApp vs
// email vs SMS — using the contacts_by_* breakdown already returned by
// the user-performance endpoint. Keeps the main table focused on totals
// while letting users drill into the channel mix on demand.
const CHANNEL_CONFIG = [
    { field: 'contacts_by_phone', label: 'Phone', icon: 'fluent:call-32-filled', color: '#10b981' },
    { field: 'contacts_by_whatsapp', label: 'WhatsApp', icon: 'ri:whatsapp-fill', color: '#25D366' },
    { field: 'contacts_by_email', label: 'Email', icon: 'ic:email', color: '#3b82f6' },
    { field: 'contacts_by_sms', label: 'SMS', icon: 'mdi:message-text', color: '#ec4899' },
    { field: 'contacts_by_other', label: 'Other', icon: 'material-symbols:more-horiz', color: '#64748b' },
];

function UserDetailPanel({ row }) {
    const total = CHANNEL_CONFIG.reduce((sum, c) => sum + (row[c.field] || 0), 0);
    if (total === 0) {
        return (
            <Box sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                    No channel-level contact breakdown for this user in the selected period.
                </Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5 }}>
                Contact Channel Breakdown — {row.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {CHANNEL_CONFIG.map((c) => {
                    const value = row[c.field] || 0;
                    const pct = total > 0 ? (value / total) * 100 : 0;
                    return (
                        <Box
                            key={c.field}
                            sx={{
                                flex: '1 1 160px',
                                minWidth: 160,
                                p: 1.5,
                                bgcolor: '#fff',
                                borderRadius: '8px',
                                border: '1px solid',
                                borderColor: alpha(c.color, 0.15),
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box sx={{
                                width: 36, height: 36, borderRadius: '8px',
                                bgcolor: alpha(c.color, 0.12),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Iconify icon={c.icon} width={20} sx={{ color: c.color }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    {c.label}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                                        {value}
                                    </Typography>
                                    <Typography sx={{ fontSize: '11px', color: '#94a3b8' }}>
                                        {pct.toFixed(0)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
UserDetailPanel.propTypes = { row: PropTypes.object };
UserDetailPanel.defaultProps = { row: {} };

// --- Main Component ---
export default function UserPerformanceTable({ practiceId, dateRange, onRetry }) {
    const userPerformance = useReduxSelector((state) => state.userPerformance.userPerformance);
    const isLoading = useReduxSelector((state) => state.userPerformance.isLoading);
    const error = useReduxSelector((state) => state.userPerformance.error);
    const selectedUserId = useReduxSelector((state) => state.userPerformance.selectedUserId);

    // Navigate to the Schedule Reports settings with USER_PERFORMANCE
    // pre-selected — Phase 3 backend adds this report type and the
    // settings page reads ?prefill=<TYPE> to auto-open the Add dialog.
    const navigate = useNavigate();

    // Modal state for drill-down
    // Popup state for cell drilldown — opens TreatmentLeadPopup in event
    // mode (eventSource) filtered by user + event type. Only funnel-stage
    // cells (Booked/Attended/FTA/Archived) reach this; activity counters
    // are non-clickable per FIELD_TO_EVENT_TYPE gating.
    const [popup, setPopup] = useState({ open: false, userId: null, userName: '', field: '' });

    // Toolbar state — mirrors ClinicianConversionTracker / LandingUrlTable
    // pattern so the UX feels consistent across analytics tables.
    const [searchQuery, setSearchQuery] = useState('');
    const [columnsAnchor, setColumnsAnchor] = useState(null);
    const [exportAnchor, setExportAnchor] = useState(null);
    // All columns visible by default; users hide individually via the
    // column-visibility Popover and the selection persists per user.
    const [hiddenColumns, setHiddenColumns] = useState([]);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Table preferences plumbing — persists hidden columns per user so the
    // table remembers their view across sessions. Reuses the existing
    // tablePreferences slice / endpoint; same pattern as LandingUrlTable.
    const userId = useGetCurrentUserId();
    const buildExportName = useReportExportFilename();
    const { tablePreferences: tablePrefsRaw, globalTablePreferences } = useReduxSelector((state) => state.tablePreferences);
    const prefResults = tablePrefsRaw?.results || tablePrefsRaw || [];

    useEffect(() => {
        if (userId) dispatch(getTablePreferences({ practiceUserId: userId }));
    }, [userId]);

    const savePreferences = useCallback((newHidden) => {
        if (!userId) return;
        dispatch(setTablePreferenceLoading(true));
        const tData = getCurrentTableData(prefResults, TABLE_NAME);
        const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
        const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
        const currentPreferences = currentTableData?.[0]?.preferences || {};
        const updatedPrefs = { ...currentPreferences, hiddenColumns: newHidden };
        const payload = {
            body: { table_name: TABLE_NAME, preferences: updatedPrefs },
            practiceUserId: userId,
        };
        if (isArray(tData) && tData.length > 0) {
            dispatch(handleTablePreferencesEdit({ ...payload, tableId: tData[0].id }));
        } else {
            dispatch(handleTablePreferences(payload));
        }
    }, [userId, prefResults, globalTablePreferences]);

    useEffect(() => {
        if (!prefsLoaded && userId) {
            const tData = getCurrentTableData(prefResults, TABLE_NAME);
            const globalData = getCurrentTableData(globalTablePreferences?.results || [], TABLE_NAME);
            const currentTableData = isArray(tData) && tData.length > 0 ? tData : globalData;
            const prefs = currentTableData?.[0]?.preferences;
            if (prefs?.hiddenColumns && isArray(prefs.hiddenColumns)) {
                setHiddenColumns(prefs.hiddenColumns);
            }
            setPrefsLoaded(true);
        }
    }, [prefResults, globalTablePreferences, userId, prefsLoaded]);

    const columnVisibilityModel = useMemo(() => {
        const model = {};
        hiddenColumns.forEach((field) => { model[field] = false; });
        return model;
    }, [hiddenColumns]);

    const isFilterActive = hiddenColumns.length > 0;

    const handleToggleColumn = useCallback((field) => {
        const updated = hiddenColumns.includes(field)
            ? hiddenColumns.filter((f) => f !== field)
            : [...hiddenColumns, field];
        setHiddenColumns(updated);
        savePreferences(updated);
    }, [hiddenColumns, savePreferences]);

    const handleRowClick = useCallback((params) => {
        const userId = params.row.id || params.row.user_id;
        dispatch(setSelectedUserId(selectedUserId === userId ? null : userId));
    }, [selectedUserId]);

    const handleCellDrillDown = useCallback((row, field) => {
        setPopup({ open: true, userId: row.id || row.user_id, userName: row.name, field });
    }, []);

    const handleClosePopup = useCallback(() => {
        setPopup((prev) => ({ ...prev, open: false }));
    }, []);

    const allRows = useMemo(() => {
        // Build the previous_* mirror object once per row via COMPARISON_FIELDS.
        // Single source of truth — adding a metric to that exported list
        // automatically threads it through here without code changes.
        const decorateWithPrevious = (row) => {
            const out = {};
            COMPARISON_FIELDS.forEach((field) => {
                out[`previous_${field}`] = row[`previous_${field}`];
            });
            return out;
        };
        return (userPerformance || []).map((row, idx) => {
            const name = row.name || row.user_name
                || [row.first_name, row.last_name].filter(Boolean).join(' ')
                || row.email
                || `User ${idx + 1}`;
            return {
                id: row.id || row.practice_user_id || row.user_id || idx,
                name,
                avatar: row.avatar || null,
                totalEvents: row.total_events || 0,
                leadsContacted: row.leads_contacted || 0,
                appointmentsBooked: row.appointments_booked || 0,
                appointmentsAttended: row.appointments_attended || 0,
                fta: row.fta || row.appointments_fta || 0,
                followUps: row.follow_ups || row.follow_ups_completed || 0,
                whatsappSent: row.whatsapp_sent || 0,
                whatsappReceived: row.whatsapp_received || 0,
                emails: row.emails_sent || row.emails || 0,
                sms: row.sms_sent || row.sms || 0,
                calls: row.calls_logged || row.calls || 0,
                payments: row.payments_received || row.payments || 0,
                // Backed by UserEvent.event_type=LEAD_ARCHIVED — fires from
                // the lead_archived signal whenever a user archives a lead.
                archived: row.leads_archived || row.archived || 0,
                // Derived efficiency ratios. Surfaces who is converting,
                // not just who is loudest. Returns null when the
                // denominator is 0 so the rateCell can render "—" rather
                // than a misleading 0% or Infinity. Capped at 100% on the
                // off chance the data has duplicates pushing booked >
                // contacted (shouldn't happen, but cheap insurance).
                bookRate: (() => {
                    const contacted = row.leads_contacted || 0;
                    const booked = row.appointments_booked || 0;
                    if (contacted === 0) return null;
                    return Math.min(100, (booked / contacted) * 100);
                })(),
                attendRate: (() => {
                    const booked = row.appointments_booked || 0;
                    const attended = row.appointments_attended || 0;
                    if (booked === 0) return null;
                    return Math.min(100, (attended / booked) * 100);
                })(),
                // Decorated previous_* fields — see decorateWithPrevious()
                // above. Spreading at the end lets the literal row fields
                // win for any overlap (none today, but defensive).
                ...decorateWithPrevious(row),
                // Channel breakdown for contacts — already returned by the
                // user-performance endpoint (contacts_by_phone, etc.). Not
                // shown in the main table (15 columns already) but surfaced
                // in the row-expand detail panel below.
                contacts_by_phone: row.contacts_by_phone || 0,
                contacts_by_email: row.contacts_by_email || 0,
                contacts_by_whatsapp: row.contacts_by_whatsapp || 0,
                contacts_by_sms: row.contacts_by_sms || 0,
                contacts_by_other: row.contacts_by_other || 0,
            };
        });
    }, [userPerformance]);

    // Search filter — name only. Matches the simplicity of the other
    // analytics tables; if users want filter-by-metric later it can grow
    // into a multi-field filter Popover.
    const rows = useMemo(() => {
        if (!searchQuery.trim()) return allRows;
        const q = searchQuery.trim().toLowerCase();
        return allRows.filter((r) => (r.name || '').toLowerCase().includes(q));
    }, [allRows, searchQuery]);

    // CSV export — builds from visible columns only (respects the user's
    // column-visibility choices). Rates render as "60%", numbers as-is,
    // "—" placeholders preserved for null ratios. Same blob/anchor
    // download pattern as LandingUrlTable / ClinicianConversionTracker.
    const handleExportCSV = useCallback(() => {
        setExportAnchor(null);
        if (!rows || rows.length === 0) return;
        const visibleCols = ALL_COLUMNS.filter((c) => !hiddenColumns.includes(c.field));
        // Always lead with "User" so downstream consumers can identify rows.
        const headers = ['User', ...visibleCols.map((c) => c.label)];
        const csvRows = rows.map((row) => {
            const cells = [row.name || ''];
            visibleCols.forEach((col) => {
                const v = row[col.field];
                if (v === null || v === undefined) {
                    cells.push('—');
                } else if (col.field === 'bookRate' || col.field === 'attendRate') {
                    cells.push(`${Math.round(v)}%`);
                } else {
                    cells.push(v);
                }
            });
            return cells;
        });
        const csvContent = [headers, ...csvRows]
            .map((cells) => cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName('user-performance', dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [rows, hiddenColumns]);

    const columns = useMemo(() => {
        // Equal-flex layout: every numeric column gets `flex: 1` with a
        // `minWidth` floor so the grid auto-distributes available width.
        // The name column gets flex: 2 because it carries the avatar +
        // full user name. The `description` prop renders a native
        // hover-tooltip on the header — used to expand abbreviated metric
        // names without making the column wider.
        //
        // Each numeric column also passes a `prevField` so the cell
        // renders a DeltaChip fed by the row's `previous_*` value.
        // `inverted: true` on FTA so a falling FTA count shows green
        // (appointments are actually getting attended, which is good).
        const numCol = (field, prevField, headerName, description, opts = {}) => ({
            field,
            headerName,
            description,
            flex: 1,
            minWidth: 130,
            align: 'center',
            headerAlign: 'center',
            ...numCellWithDelta(field, prevField, handleCellDrillDown, opts),
        });
        // Derived-ratio column: same flex/minWidth conventions but uses
        // rateCell (no DeltaChip, no drill-down, threshold colouring).
        const rateCol = (field, headerName, description) => ({
            field,
            headerName,
            description,
            flex: 1,
            minWidth: 100,
            align: 'center',
            headerAlign: 'center',
            ...rateCell(),
        });
        return [
            {
                field: 'name',
                headerName: 'User Name',
                flex: 2,
                minWidth: 200,
                renderCell: (params) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                        <Avatar
                            src={params.row.avatar}
                            sx={{
                                width: 32,
                                height: 32,
                                bgcolor: '#e2e8f0',
                                color: '#64748b',
                                fontWeight: 600,
                                fontSize: '12px',
                            }}
                        >
                            {getInitials(params.value)}
                        </Avatar>
                        <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                            {params.value}
                        </Typography>
                    </Box>
                ),
            },
            // Order: Booked + Archived first per client direction —
            // they're the two volume metrics that matter most for at-a-glance
            // team performance. The rest of the funnel and activity stack
            // follows. Order matches ALL_COLUMNS (and therefore the
            // column-visibility Popover order + CSV export header order).
            //
            // drillable=false: activity counters (events / messages / calls
            // / payments / follow-ups / rate metrics) have no honest
            // single lead-list interpretation, so their cells are
            // non-clickable. drillable=true (default): funnel-stage cells
            // map to a UserEvent type via FIELD_TO_EVENT_TYPE and open the
            // unified TreatmentLeadPopup in event mode.
            numCol('appointmentsBooked', 'previous_appointments_booked', 'Appts Booked', 'Appointments booked by this user — click to drill into the lead list'),
            numCol('archived', 'previous_leads_archived', 'Archived', 'Leads archived by this user — click to drill into the lead list'),
            numCol('totalEvents', 'previous_total_events', 'Total Events', 'Total tracked events across all activities', { drillable: false }),
            numCol('leadsContacted', 'previous_leads_contacted', 'Contacted', 'Unique leads contacted by this user', { drillable: false }),
            rateCol('bookRate', 'Book Rate', 'Appointments booked ÷ leads contacted — efficiency of this user\'s contact-to-booking funnel. "—" when no contacts.'),
            rateCol('attendRate', 'Attend Rate', 'Appointments attended ÷ appointments booked — efficiency of this user\'s booking-to-attendance funnel. "—" when no bookings.'),
            numCol('appointmentsAttended', 'previous_appointments_attended', 'Appts Attended', 'Appointments attended (consultations + treatments) — click to drill into the lead list'),
            numCol('fta', 'previous_appointments_fta', 'FTA', 'Failed to Attend — click to drill into leads who did not show up', { inverted: true }),
            numCol('followUps', 'previous_follow_ups_completed', 'Follow-ups', 'Follow-up tasks completed by this user', { drillable: false }),
            numCol('whatsappSent', 'previous_whatsapp_sent', 'WA Sent', 'WhatsApp messages sent by this user', { drillable: false }),
            numCol('whatsappReceived', 'previous_whatsapp_received', 'WA Recd', 'WhatsApp messages received from leads handled by this user', { drillable: false }),
            numCol('emails', 'previous_emails_sent', 'Emails', 'Emails sent by this user', { drillable: false }),
            numCol('sms', 'previous_sms_sent', 'SMS', 'SMS messages sent by this user', { drillable: false }),
            numCol('calls', 'previous_calls_logged', 'Calls', 'Phone calls logged by this user', { drillable: false }),
            numCol('payments', 'previous_payments_received', 'Payments', 'Payments collected / processed by this user', { drillable: false }),
        ];
    }, [handleCellDrillDown]);

    return (
        <Box
            sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
                    User Performance
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <TextField
                        size="small"
                        placeholder="Search user..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ width: 220 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-outline" width={20} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                    />

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    <Tooltip title="Show/hide columns" arrow>
                        <Badge
                            variant="dot"
                            invisible={!isFilterActive}
                            sx={{
                                '& .MuiBadge-badge': {
                                    bgcolor: '#3b82f6',
                                    width: 8,
                                    height: 8,
                                    minWidth: 8,
                                    top: 4,
                                    right: 4,
                                },
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={(e) => setColumnsAnchor(e.currentTarget)}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    border: '1px solid',
                                    borderColor: (isFilterActive || columnsAnchor) ? 'primary.main' : '#e2e8f0',
                                    borderRadius: '8px',
                                    bgcolor: columnsAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:options-2-outline" width={16} sx={{ color: (isFilterActive || columnsAnchor) ? 'primary.main' : '#64748b' }} />
                            </IconButton>
                        </Badge>
                    </Tooltip>

                    <Tooltip title="Export data" arrow>
                        <IconButton
                            size="small"
                            onClick={(e) => setExportAnchor(e.currentTarget)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: exportAnchor ? 'primary.main' : '#e2e8f0',
                                borderRadius: '8px',
                                bgcolor: exportAnchor ? alpha('#3b82f6', 0.08) : 'transparent',
                                '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                            }}
                        >
                            <Iconify icon="eva:download-outline" width={16} sx={{ color: exportAnchor ? 'primary.main' : '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    {/* Schedule email report — Phase 3 wires this widget to
                        the new USER_PERFORMANCE report type. Settings page
                        reads ?prefill=USER_PERFORMANCE, auto-opens Add
                        dialog with the report type pre-selected. */}
                    <Tooltip title="Schedule this report via email" arrow>
                        <IconButton
                            size="small"
                            onClick={() => navigate(`${PATH_DASHBOARD.analyticsReports.settings}?prefill=USER_PERFORMANCE`)}
                            sx={{
                                width: 32,
                                height: 32,
                                border: '1px solid',
                                borderColor: '#e2e8f0',
                                borderRadius: '8px',
                                '&:hover': { bgcolor: alpha('#7c3aed', 0.08), borderColor: '#7c3aed' },
                            }}
                        >
                            <Iconify icon="mdi:email-fast-outline" width={16} sx={{ color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>

                    <Popover
                        open={Boolean(columnsAnchor)}
                        anchorEl={columnsAnchor}
                        onClose={() => setColumnsAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', width: 240, maxHeight: 400 } } }}
                    >
                        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                                Columns
                            </Typography>
                        </Box>
                        <List dense sx={{ py: 0.5 }}>
                            {ALL_COLUMNS.map((col) => {
                                const isVisible = !hiddenColumns.includes(col.field);
                                return (
                                    <ListItemButton
                                        key={col.field}
                                        onClick={() => handleToggleColumn(col.field)}
                                        sx={{ py: 0.25, px: 1.5, opacity: isVisible ? 1 : 0.6 }}
                                    >
                                        <Checkbox size="small" checked={isVisible} sx={{ p: 0, mr: 1 }} />
                                        <ListItemText
                                            primary={col.label}
                                            primaryTypographyProps={{ fontSize: '13px', color: isVisible ? '#1e293b' : '#94a3b8' }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Popover>

                    <Popover
                        open={Boolean(exportAnchor)}
                        anchorEl={exportAnchor}
                        onClose={() => setExportAnchor(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        slotProps={{ paper: { sx: { mt: 1, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 200 } } }}
                    >
                        <List dense sx={{ py: 0.5 }}>
                            <ListItemButton onClick={handleExportCSV} sx={{ py: 1, px: 2 }}>
                                <Iconify icon="mdi:file-delimited-outline" width={18} sx={{ color: '#64748b', mr: 1.5 }} />
                                <ListItemText primary="Export CSV" primaryTypographyProps={{ fontSize: '13px' }} />
                            </ListItemButton>
                        </List>
                    </Popover>
                </Box>
            </Box>

            <Box>
                <DataGridPro
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pagination
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    columnVisibilityModel={columnVisibilityModel}
                    // 3-click sort cycle (none → desc → asc → none) matches
                    // ClinicianConversionTracker's pattern. desc-first because
                    // the user almost always wants "top performers" not
                    // "bottom performers" on first click.
                    sortingOrder={['desc', 'asc', null]}
                    // Per-row expand: chevron in the leading column reveals
                    // a panel with channel-split for that user. Uses the
                    // existing contacts_by_* fields from the API response;
                    // no extra fetch.
                    getDetailPanelContent={({ row }) => <UserDetailPanel row={row} />}
                    getDetailPanelHeight={() => 'auto'}
                    getRowId={(row) => row.id}
                    onRowClick={handleRowClick}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            fontSize: '12px',
                            color: '#64748b',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f1f5f9',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                            cursor: 'pointer',
                        },
                        '& .MuiDataGrid-row': {
                            transition: 'background-color 0.15s',
                        },
                    }}
                    getRowClassName={(params) =>
                        selectedUserId && (params.row.id === selectedUserId) ? 'Mui-selected' : ''
                    }
                />
            </Box>

            {error && !isLoading && (
                <Box
                    sx={{
                        bgcolor: alpha('#ef4444', 0.06),
                        border: '1px solid',
                        borderColor: alpha('#ef4444', 0.2),
                        borderRadius: 1,
                        mx: 2.5,
                        my: 2,
                        px: 2,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Iconify icon="mdi:alert-circle-outline" width={20} sx={{ color: '#ef4444' }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                            Failed to load team performance
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                            {error}
                        </Typography>
                    </Box>
                    {onRetry && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={onRetry}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            Retry
                        </Button>
                    )}
                </Box>
            )}

            {rows.length === 0 && !isLoading && !error && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ color: '#64748b' }}>
                        No team activity in the selected date range.
                    </Typography>
                </Box>
            )}

            {/*
              * Cell-drilldown popup. Same TreatmentLeadPopup used by every
              * other analytics tab (Overview / By Source / By Treatment /
              * By Practitioner) — one popup style across the app per the
              * consistency directive. These columns count events BY ACTOR
              * (apps/reports/views/user_performance.py:141), so the popup
              * reads the UserEvent API via eventSource instead of filtering
              * leads by assignee — filtering leads by assignee cannot
              * reconcile with these counters and returned 0 rows for every
              * user. Filters by:
              *   - practiceUserId: the clicked user's ID
              *   - eventType: derived from the clicked column via FIELD_TO_EVENT_TYPE
              *   - startDate / endDate: passed through from the page
              */}
            <TreatmentLeadPopup
                open={popup.open}
                onClose={handleClosePopup}
                practiceId={practiceId}
                treatmentName={`${popup.userName} — ${COLUMN_LABEL[popup.field] || popup.field}`}
                eventSource={{
                    practiceUserId: popup.userId,
                    eventType: FIELD_TO_EVENT_TYPE[popup.field],
                    startDate: dateRange?.startDate ? moment(dateRange.startDate).format('YYYY-MM-DD') : undefined,
                    endDate: dateRange?.endDate ? moment(dateRange.endDate).format('YYYY-MM-DD') : undefined,
                }}
            />
        </Box>
    );
}
